import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  ProjectAssignableEmployee,
  ProjectAssignee,
  ProjectBoard,
  ProjectBoardColumn,
  ProjectColumn,
  ProjectCreateInput,
  ProjectDetails,
  ProjectListFilters,
  ProjectListItem,
  ProjectMember,
  ProjectMemberRole,
  ProjectMembersAddInput,
  ProjectSearchResult,
  ProjectSubtask,
  ProjectSubtaskWriteInput,
  ProjectTask,
  ProjectTaskCreateInput,
  ProjectTaskUpdateInput,
  CompletedFocusBoard,
  TaskFocusSessionInput,
  TaskTimeLog,
  TaskTimeLogCreateInput,
  TaskTimeLogList,
  TaskTimeLogSource,
} from '../types/project';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';
import { calendarDateFromApi } from '../utils/dateTime';
import {
  isProjectMemberRole,
  normalizeStoredTaskLimit,
  projectPersonName,
} from '../utils/projectForm';
import { applyColumnTaskRules } from '../utils/projectTaskForm';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function nestedObject(value: unknown): ObjectValue | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as ObjectValue)
    : null;
}

function normalizeMemberRole(value: unknown): ProjectMemberRole {
  const text = textOrNull(value)?.toUpperCase() ?? '';
  if (isProjectMemberRole(text)) return text;
  throw Object.assign(new Error('Member role is invalid'), {
    code: 'VALIDATION_ERROR',
  });
}

function normalizeListItem(row: ObjectValue): ProjectListItem | null {
  try {
    const isActive = boolOrNull(row.isActive);
    const statusCode = textOrNull(row.statusCode);
    return {
      projectId: requiredId(row.projectId, 'projectId'),
      projectName: textOrNull(row.projectName) ?? 'Project',
      description: textOrNull(row.description),
      memberCount: numberOrNull(row.memberCount) ?? 0,
      statusCode,
      statusName: textOrNull(row.statusName),
      statusLabel:
        textOrNull(row.statusName) ??
        getMasterDataStatusLabel(isActive, statusCode),
      isActive,
      createdAt: textOrNull(row.createdAt),
      rowVersion: numberOrNull(row.rowVersion),
    };
  } catch {
    return null;
  }
}

function normalizeMember(row: ObjectValue): ProjectMember {
  return {
    projectMemberId: numberOrNull(row.projectMemberId),
    employeeId: requiredId(row.employeeId, 'employeeId'),
    employeeNumber: textOrNull(row.employeeNumber),
    employeeName: projectPersonName({
      employeeName: textOrNull(row.employeeName),
      displayName: textOrNull(row.displayName),
      employeeNumber: textOrNull(row.employeeNumber),
      firstName: textOrNull(row.firstName),
      middleName: textOrNull(row.middleName),
      lastName: textOrNull(row.lastName),
    }),
    memberRole: normalizeMemberRole(row.memberRole ?? row.role),
  };
}

function normalizeColumn(row: ObjectValue, index: number): ProjectColumn {
  return {
    projectColumnId: numberOrNull(row.projectColumnId),
    columnName: textOrNull(row.columnName) ?? `Column ${index + 1}`,
    displayOrder: numberOrNull(row.displayOrder) ?? index + 1,
    taskLimit: normalizeStoredTaskLimit(numberOrNull(row.taskLimit)),
    isCompletedColumn: boolOrNull(row.isCompletedColumn) === true,
    isDefaultColumn: boolOrNull(row.isDefaultColumn) === true,
    requiresTaskDetails: boolOrNull(row.requiresTaskDetails) === true,
    taskCount: numberOrNull(row.taskCount) ?? undefined,
  };
}

function normalizeAssignee(row: ObjectValue, index: number): ProjectAssignee | null {
  try {
    const assignedRole = textOrNull(row.memberRole)?.toUpperCase() ?? '';
    return {
      employeeId: requiredId(row.employeeId, 'employeeId'),
      employeeName: projectPersonName({
        employeeName: textOrNull(row.employeeName),
        displayName: textOrNull(row.displayName),
        employeeNumber: textOrNull(row.employeeNumber),
        firstName: textOrNull(row.firstName),
        middleName: textOrNull(row.middleName),
        lastName: textOrNull(row.lastName),
      }),
      memberRole: isProjectMemberRole(assignedRole) ? assignedRole : null,
      displayOrder: numberOrNull(row.displayOrder) ?? index + 1,
    };
  } catch {
    return null;
  }
}

function normalizeAssignees(value: unknown): ProjectAssignee[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) =>
      item && typeof item === 'object' && !Array.isArray(item)
        ? normalizeAssignee(item as ObjectValue, index)
        : null,
    )
    .filter((item): item is ProjectAssignee => item != null);
}

function timestampText(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  return textOrNull(value) ?? '';
}

function normalizeTimeLog(row: ObjectValue): TaskTimeLog | null {
  try {
    const raw = textOrNull(row.source)?.toUpperCase();
    const source: TaskTimeLogSource =
      raw === 'POMODORO' || raw === 'DEEP_FOCUS' ? raw : 'MANUAL';
    return {
      timeLogId: requiredId(row.timeLogId, 'timeLogId'),
      taskId: requiredId(row.taskId, 'taskId'),
      employeeId: requiredId(row.employeeId, 'employeeId'),
      employeeName: textOrNull(row.employeeName) ?? 'Employee',
      startedAt: timestampText(row.startedAt),
      endedAt: timestampText(row.endedAt),
      durationMinutes: numberOrNull(row.durationMinutes) ?? 0,
      source,
    };
  } catch {
    return null;
  }
}

function normalizeSubtask(row: ObjectValue, index: number): ProjectSubtask {
  return {
    subtaskId: numberOrNull(row.subtaskId),
    taskId: numberOrNull(row.taskId),
    subtaskName: textOrNull(row.subtaskName) ?? `Subtask ${index + 1}`,
    displayOrder: numberOrNull(row.displayOrder) ?? index + 1,
    isCompleted: boolOrNull(row.isCompleted) === true,
    assignees: normalizeAssignees(row.assignees),
  };
}

function fallbackAssignees(task: {
  assignedEmployeeId: number | null;
  assignedEmployeeName: string | null;
  assignedMemberRole: ProjectMemberRole | null;
}): ProjectAssignee[] {
  if (!task.assignedEmployeeId) return [];
  return [
    {
      employeeId: task.assignedEmployeeId,
      employeeName: task.assignedEmployeeName ?? 'Employee',
      memberRole: task.assignedMemberRole,
      displayOrder: 1,
    },
  ];
}

function normalizeTask(row: ObjectValue): ProjectTask {
  const subtasks = Array.isArray(row.subtasks)
    ? row.subtasks.map((item, index) =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? normalizeSubtask(item as ObjectValue, index)
          : null,
      ).filter((item): item is ProjectSubtask => item != null)
    : [];
  const assignedRole = textOrNull(row.assignedMemberRole)?.toUpperCase() ?? '';
  const assignedEmployeeId = numberOrNull(row.assignedEmployeeId);
  const assignedEmployeeName = textOrNull(row.assignedEmployeeName);
  const assignedMemberRole = isProjectMemberRole(assignedRole) ? assignedRole : null;
  const assignees = normalizeAssignees(row.assignees);
  return {
    taskId: requiredId(row.taskId, 'taskId'),
    projectId: requiredId(row.projectId, 'projectId'),
    projectColumnId: requiredId(row.projectColumnId, 'projectColumnId'),
    taskName: textOrNull(row.taskName) ?? 'Task',
    assignedEmployeeId: assignedEmployeeId ?? assignees[0]?.employeeId ?? null,
    assignedEmployeeName: assignedEmployeeName ?? assignees[0]?.employeeName ?? null,
    assignedMemberRole: assignedMemberRole ?? assignees[0]?.memberRole ?? null,
    assignees: assignees.length
      ? assignees
      : fallbackAssignees({
          assignedEmployeeId,
          assignedEmployeeName,
          assignedMemberRole,
        }),
    dueDate: calendarDateFromApi(row.dueDate),
    completedDate: calendarDateFromApi(
      row.completedDate ?? row.completedOn ?? row.completionDate,
    ),
    timeEstimateMinutes: numberOrNull(row.timeEstimateMinutes),
    timeSpentMinutes: numberOrNull(row.timeSpentMinutes) ?? 0,
    description: textOrNull(row.description),
    displayOrder: numberOrNull(row.displayOrder) ?? 0,
    subtaskTotal: numberOrNull(row.subtaskTotal) ?? subtasks.length,
    subtaskCompleted:
      numberOrNull(row.subtaskCompleted) ??
      subtasks.filter(item => item.isCompleted).length,
    subtasks,
    createdByEmployeeId: numberOrNull(row.createdByEmployeeId),
    createdByName: textOrNull(row.createdByName),
  };
}

function mapDetails(data: {
  project?: ObjectValue;
  members?: ObjectValue[];
  columns?: ObjectValue[];
}): ProjectDetails {
  const project = nestedObject(data.project);
  if (!project) {
    throw Object.assign(new Error('Project details are incomplete'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const members = Array.isArray(data.members) ? data.members : [];
  const listItem = normalizeListItem({
    ...project,
    memberCount: members.length,
  });
  if (!listItem) {
    throw Object.assign(new Error('Project details are incomplete'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const columns = Array.isArray(data.columns)
    ? data.columns.map((row, index) => normalizeColumn(row, index))
    : [];
  columns.sort((left, right) => left.displayOrder - right.displayOrder);
  return {
    projectId: listItem.projectId,
    projectName: listItem.projectName,
    description: listItem.description,
    statusCode: listItem.statusCode,
    statusName: listItem.statusName,
    statusLabel: listItem.statusLabel,
    isActive: listItem.isActive,
    createdAt: listItem.createdAt,
    updatedAt: textOrNull(project.updatedAt),
    rowVersion: listItem.rowVersion,
    members: members.map(normalizeMember),
    columns: applyColumnTaskRules(columns),
  };
}

function mapBoard(data: {
  project?: ObjectValue;
  members?: ObjectValue[];
  columns?: ObjectValue[];
}): ProjectBoard {
  const details = mapDetails(data);
  const sourceColumns = Array.isArray(data.columns) ? data.columns : [];
  const tasksByColumn = new Map<number, ProjectTask[]>();
  for (const source of sourceColumns) {
    const columnId = numberOrNull(source.projectColumnId);
    if (columnId == null) continue;
    const tasks = Array.isArray(source.tasks)
      ? source.tasks
          .map(row => {
            try {
              return nestedObject(row) ? normalizeTask(row as ObjectValue) : null;
            } catch {
              return null;
            }
          })
          .filter((item): item is ProjectTask => item != null)
      : [];
    tasks.sort((left, right) => left.displayOrder - right.displayOrder);
    tasksByColumn.set(columnId, tasks);
  }
  const boardColumns: ProjectBoardColumn[] = details.columns.map(column => {
    const tasks =
      column.projectColumnId != null
        ? tasksByColumn.get(column.projectColumnId) ?? []
        : [];
    return {
      ...column,
      taskCount: column.taskCount ?? tasks.length,
      tasks,
    };
  });
  return {
    projectId: details.projectId,
    projectName: details.projectName,
    description: details.description,
    members: details.members,
    columns: boardColumns,
  };
}

function buildListParams(
  page: number,
  pageSize: number,
  filters: ProjectListFilters | undefined,
) {
  const params: Record<string, string | number | boolean> = { page, pageSize };
  const search = filters?.search?.trim();
  if (search) params.search = search;
  if (filters?.isActive === true) params.isActive = true;
  if (filters?.isActive === false) params.isActive = false;
  return params;
}

export async function getProjects(
  options: {
    page?: number;
    pageSize?: number;
    filters?: ProjectListFilters;
  } = {},
): Promise<ProjectSearchResult> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options.pageSize && options.pageSize > 0 ? options.pageSize : 20;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: number;
        pageSize?: number;
        totalRecords?: number;
        totalPages?: number;
      };
    }>
  >(API_ENDPOINTS.PROJECTS.LIST, {
    params: buildListParams(page, pageSize, options.filters),
  });

  const data = response.data.data ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeListItem)
        .filter((item): item is ProjectListItem => item != null)
    : [];
  const pagination = data.pagination ?? {};
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/** Team Lead only: the employees of the signed-in lead's teams (resolved by the API). */
export async function getProjectAssignableEmployees(
  search?: string,
): Promise<ProjectAssignableEmployee[]> {
  const term = search?.trim();
  const response = await apiClient.get<Envelope<{ items?: ObjectValue[] }>>(
    API_ENDPOINTS.PROJECTS.ASSIGNABLE_EMPLOYEES,
    { params: term ? { search: term } : {} },
  );
  const items = response.data.data?.items;
  if (!Array.isArray(items)) return [];
  return items.flatMap(row => {
    const employeeId = numberOrNull(row.employeeId);
    if (employeeId === null || employeeId <= 0) return [];
    const employeeNumber = textOrNull(row.employeeNumber);
    return [
      {
        employeeId,
        employeeNumber,
        displayName: projectPersonName({
          displayName: textOrNull(row.displayName),
          employeeNumber,
          firstName: textOrNull(row.firstName),
          middleName: textOrNull(row.middleName),
          lastName: textOrNull(row.lastName),
        }),
      },
    ];
  });
}

export async function getProjectDetails(
  projectId: number,
): Promise<ProjectDetails> {
  const response = await apiClient.get<
    Envelope<{
      project?: ObjectValue;
      members?: ObjectValue[];
      columns?: ObjectValue[];
    }>
  >(API_ENDPOINTS.PROJECTS.DETAILS(projectId));
  return mapDetails(response.data.data ?? {});
}

export async function createProject(
  input: ProjectCreateInput,
): Promise<ProjectDetails> {
  const response = await apiClient.post<
    Envelope<{
      project?: ObjectValue;
      members?: ObjectValue[];
      columns?: ObjectValue[];
    }>
  >(API_ENDPOINTS.PROJECTS.CREATE, input);
  return mapDetails(response.data.data ?? {});
}

/**
 * Edit Project: adds new members to an existing project and returns the
 * project as saved, including every member (existing + new).
 */
export async function addProjectMembers(
  projectId: number,
  input: ProjectMembersAddInput,
): Promise<ProjectDetails> {
  const response = await apiClient.post<
    Envelope<{
      project?: ObjectValue;
      members?: ObjectValue[];
      columns?: ObjectValue[];
    }>
  >(API_ENDPOINTS.PROJECTS.ADD_MEMBERS(projectId), input);
  return mapDetails(response.data.data ?? {});
}

export async function getProjectBoard(projectId: number): Promise<ProjectBoard> {
  const response = await apiClient.get<
    Envelope<{
      project?: ObjectValue;
      members?: ObjectValue[];
      columns?: ObjectValue[];
    }>
  >(API_ENDPOINTS.PROJECTS.BOARD(projectId));
  return mapBoard(response.data.data ?? {});
}

function normalizeTaskList(value: unknown): ProjectTask[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(row =>
      row && typeof row === 'object' && !Array.isArray(row)
        ? normalizeTask(row as ObjectValue)
        : null,
    )
    .filter((item): item is ProjectTask => item != null);
}

export async function getCompletedFocusTasks(
  projectId: number,
  date: string,
): Promise<CompletedFocusBoard> {
  const response = await apiClient.get<
    Envelope<{
      selectedDate?: unknown;
      previousDate?: unknown;
      todayTasks?: unknown;
      yesterdayTasks?: unknown;
    }>
  >(API_ENDPOINTS.PROJECTS.COMPLETED_TASKS(projectId), { params: { date } });
  const data = response.data.data ?? {};
  const selectedDate =
    calendarDateFromApi(data.selectedDate) ?? calendarDateFromApi(date) ?? date;
  const previousDate = calendarDateFromApi(data.previousDate) ?? selectedDate;
  return {
    selectedDate,
    previousDate,
    todayTasks: normalizeTaskList(data.todayTasks),
    yesterdayTasks: normalizeTaskList(data.yesterdayTasks),
  };
}

function unwrapTask(data: { task?: ObjectValue } | undefined): ProjectTask {
  const task = nestedObject(data?.task);
  if (!task) {
    throw Object.assign(new Error('Task details are incomplete'), {
      code: 'VALIDATION_ERROR',
    });
  }
  return normalizeTask(task);
}

export async function getProjectTask(
  projectId: number,
  taskId: number,
): Promise<ProjectTask> {
  const response = await apiClient.get<Envelope<{ task?: ObjectValue }>>(
    API_ENDPOINTS.PROJECTS.TASK_DETAILS(projectId, taskId),
  );
  return unwrapTask(response.data.data);
}

export async function createProjectTask(
  projectId: number,
  input: ProjectTaskCreateInput,
): Promise<ProjectTask> {
  const response = await apiClient.post<Envelope<{ task?: ObjectValue }>>(
    API_ENDPOINTS.PROJECTS.CREATE_TASK(projectId),
    input,
  );
  return unwrapTask(response.data.data);
}

export async function updateProjectTask(
  projectId: number,
  taskId: number,
  input: ProjectTaskUpdateInput,
): Promise<ProjectTask> {
  const response = await apiClient.put<Envelope<{ task?: ObjectValue }>>(
    API_ENDPOINTS.PROJECTS.UPDATE_TASK(projectId, taskId),
    input,
  );
  return unwrapTask(response.data.data);
}

export async function moveProjectTask(
  projectId: number,
  taskId: number,
  projectColumnId: number,
  input: { completedDate?: string | null } = {},
): Promise<ProjectTask> {
  const response = await apiClient.put<Envelope<{ task?: ObjectValue }>>(
    API_ENDPOINTS.PROJECTS.MOVE_TASK(projectId, taskId),
    {
      projectColumnId,
      ...(input.completedDate !== undefined
        ? { completedDate: input.completedDate }
        : {}),
    },
  );
  return unwrapTask(response.data.data);
}

export async function createProjectSubtask(
  projectId: number,
  taskId: number,
  input: ProjectSubtaskWriteInput,
): Promise<ProjectTask> {
  const response = await apiClient.post<Envelope<{ task?: ObjectValue }>>(
    API_ENDPOINTS.PROJECTS.CREATE_SUBTASK(projectId, taskId),
    input,
  );
  return unwrapTask(response.data.data);
}

export async function updateProjectSubtask(
  projectId: number,
  subtaskId: number,
  input: ProjectSubtaskWriteInput,
): Promise<ProjectTask> {
  const response = await apiClient.put<Envelope<{ task?: ObjectValue }>>(
    API_ENDPOINTS.PROJECTS.UPDATE_SUBTASK(projectId, subtaskId),
    input,
  );
  return unwrapTask(response.data.data);
}

export async function reorderProjectSubtasks(
  projectId: number,
  taskId: number,
  subtaskIds: number[],
): Promise<ProjectTask> {
  const response = await apiClient.put<Envelope<{ task?: ObjectValue }>>(
    API_ENDPOINTS.PROJECTS.REORDER_SUBTASKS(projectId, taskId),
    { subtaskIds },
  );
  return unwrapTask(response.data.data);
}

export async function listTaskTimeLogs(
  projectId: number,
  taskId: number,
): Promise<TaskTimeLogList> {
  const response = await apiClient.get<
    Envelope<{ logs?: ObjectValue[]; timeSpentMinutes?: unknown }>
  >(API_ENDPOINTS.PROJECTS.TIME_LOGS(projectId, taskId));
  const data = response.data.data;
  const logs = Array.isArray(data?.logs)
    ? data.logs
        .map(row =>
          row && typeof row === 'object' && !Array.isArray(row)
            ? normalizeTimeLog(row)
            : null,
        )
        .filter((item): item is TaskTimeLog => item != null)
    : [];
  return {
    logs,
    timeSpentMinutes: numberOrNull(data?.timeSpentMinutes) ?? 0,
  };
}

export async function addTaskTimeLog(
  projectId: number,
  taskId: number,
  input: TaskTimeLogCreateInput,
): Promise<{ log: TaskTimeLog | null; timeSpentMinutes: number }> {
  const response = await apiClient.post<
    Envelope<{ log?: ObjectValue; timeSpentMinutes?: unknown }>
  >(API_ENDPOINTS.PROJECTS.TIME_LOGS(projectId, taskId), input);
  const data = response.data.data;
  return {
    log:
      data?.log && typeof data.log === 'object'
        ? normalizeTimeLog(data.log)
        : null,
    timeSpentMinutes: numberOrNull(data?.timeSpentMinutes) ?? 0,
  };
}

/** Logs a finished focus session as a time entry on the task. */
export async function logTaskFocusSession(
  projectId: number,
  taskId: number,
  input: TaskFocusSessionInput,
): Promise<{ log: TaskTimeLog | null; timeSpentMinutes: number }> {
  const response = await apiClient.post<
    Envelope<{ log?: ObjectValue; timeSpentMinutes?: unknown }>
  >(API_ENDPOINTS.PROJECTS.FOCUS_SESSIONS(projectId, taskId), input);
  const data = response.data.data;
  return {
    log:
      data?.log && typeof data.log === 'object'
        ? normalizeTimeLog(data.log)
        : null,
    timeSpentMinutes: numberOrNull(data?.timeSpentMinutes) ?? 0,
  };
}

export async function deleteTaskTimeLog(
  projectId: number,
  taskId: number,
  timeLogId: number,
): Promise<{ timeSpentMinutes: number }> {
  const response = await apiClient.delete<Envelope<{ timeSpentMinutes?: unknown }>>(
    API_ENDPOINTS.PROJECTS.DELETE_TIME_LOG(projectId, taskId, timeLogId),
  );
  return {
    timeSpentMinutes: numberOrNull(response.data.data?.timeSpentMinutes) ?? 0,
  };
}
